import { useState, useEffect } from 'react';
import { buildApiUrl } from '../../../utils/apiUtils';
import { useEnvironmentConfig } from '../../../hooks/useEnvironmentConfig';
import { API_ENDPOINTS } from '../utils/constants';

/**
 * Hook for managing Stripe checkout session state
 * @returns {object} Session state and utilities
 */
export const useCheckoutSession = () => {
  const [status, setStatus] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [purchasedProduct, setPurchasedProduct] = useState(null);
  const { isDev } = useEnvironmentConfig();

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      const statusUrl = `${buildApiUrl(API_ENDPOINTS.SESSION_STATUS)}?session_id=${sessionId}`;

      fetch(statusUrl)
        .then(res => res.json())
        .then(data => {
          setStatus(data.status);
          setCustomerEmail(data.customer_email || '');

          // If we have product info in the response, set it
          if (data.product) {
            setPurchasedProduct(data.product);
          }
        })
        .catch(err => {
          console.error('Error checking session status:', err);
          setStatus('error');

          // For development/demo purposes only, simulate a successful checkout
          // if (isDev) {
          //   console.warn('DEV MODE: Simulating successful checkout despite error');
          //   setStatus('complete');
          //   setCustomerEmail(DEV_FALLBACK.EMAIL);
          //   setPurchasedProduct(DEV_FALLBACK.PRODUCT);
          // } 
        });
    }
  }, [isDev]);

  /**
   * Resend email for the current session
   */
  const resendEmail = async () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      throw new Error('No session ID found');
    }

    const resendUrl = buildApiUrl(`${API_ENDPOINTS.RESEND_EMAIL}/${sessionId}`);

    const response = await fetch(resendUrl);

    if (!response.ok) {
      throw new Error(`Failed to resend email: ${response.status}`);
    }

    return response.json();
  };

  return {
    status,
    customerEmail,
    purchasedProduct,
    resendEmail,
    sessionId: new URLSearchParams(window.location.search).get('session_id'),
  };
};
